import React from "react";
import { useTranslation } from "react-i18next";

import PendingPoints from "./PendingPoints";

/**
 * The "smart snap": the moment the server recognises this phone number.
 *
 * Recognition is SERVER-side now (POST /loyalty/lookup), not a localStorage
 * flag, so it survives a borrowed phone, a cleared browser and a table QR this
 * guest has never scanned before.
 *
 * Points wording is deliberate and load-bearing. Nothing is minted at check-in
 * any more — every point is credited when the cashier marks the order paid — so
 * this card never says "+X earned". Saying "earned" here would be the app
 * claiming credit for something a cashier has not yet witnessed, and the guest
 * would go looking for a balance that isn't there.
 *
 * The pending number moved OUT of the sub-line and into its own chip
 * (PendingPoints). Sitting here as plain text inside the jade card, beside the
 * real balance, it read as money already in the account.
 */
export default function Recognition({ state, name, balance, pending, currencyNote }) {
  const { t } = useTranslation();

  if (state === "checking") {
    return (
      <div className="ss-snap checking" aria-live="polite">
        <span className="ss-snap-spin" aria-hidden="true" />
        <span className="ss-snap-tx">{t("scan_stay.order.checking")}</span>
      </div>
    );
  }

  if (state === "known") {
    return (
      <div className="ss-snap known" aria-live="polite">
        <span className="ss-snap-ic" aria-hidden="true">✓</span>
        <div className="ss-snap-body">
          <div className="ss-snap-hi">{t("scan_stay.order.welcome_back", { name })}</div>
          <div className="ss-snap-sub">{t("scan_stay.order.points_will_add")}</div>
          {typeof balance === "number" && (
            <div className="ss-snap-bal">
              <span className="ss-snap-bal-n">{balance}</span>
              <span className="ss-snap-bal-l">{t("scan_stay.your_points")}</span>
            </div>
          )}
          <PendingPoints points={pending} />
        </div>
      </div>
    );
  }

  if (state === "new") {
    return (
      <div className="ss-snap fresh" aria-live="polite">
        <span className="ss-snap-ic" aria-hidden="true">✨</span>
        <div className="ss-snap-body">
          <div className="ss-snap-hi">{t("scan_stay.order.first_time")}</div>
          <div className="ss-snap-sub">{currencyNote}</div>
          <PendingPoints points={pending} />
        </div>
      </div>
    );
  }

  return null;
}
